/**
 * Public Events & Classes Page
 *
 * Shows upcoming events (cakes decorating classes, parties, etc.) that
 * Tammy has marked visible from Admin → Events & Classes.
 *
 * URL: /events
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon, ClockIcon, MapPinIcon, TicketIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { usePageSeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE, PAGE_SEO } from '../utils/seoDefaults';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';

const API_URL = import.meta.env.VITE_API_URL || '';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventItem {
  id: string;
  title: string;
  description: string | null;
  event_date: string | null;
  event_time: string | null;
  location: string | null;
  price_text: string | null;
  price: number | null;
  spots_total: number | null;
  spots_remaining: number | null;
  image_url: string | null;
  registration_url: string | null;
  is_featured: boolean;
  category?: {
    name: string;
    color: string;
  } | null;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

/** "2026-05-08" → "Thursday, May 8, 2026" */
function formatDate(iso: string | null) {
  if (!iso) return null;
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function EventsPage() {
  const { getImage } = useSiteImageOverrides();
  usePageSeo({
    ...PAGE_SEO.events,
    og: {
      type: 'website',
      title: PAGE_SEO.events.title,
      description: PAGE_SEO.events.description,
      image: getImage('og_image', DEFAULT_OG_IMAGE),
    },
  });
  const [events, setEvents]   = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/events?upcoming=true&visible=true`)
      .then(r => r.json())
      .then(d => setEvents(d.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-bakery-cream">
      {/* Hero banner */}
      <section className="bg-gradient-to-br from-purple-700 to-bakery-rose text-white py-16">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-6xl mb-4">🎂</div>
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-3">
              Events &amp; Classes
            </h1>
            <p className="text-xl text-white/80 max-w-xl mx-auto">
              Learn to decorate, celebrate a birthday, or just have fun with cake!
              Join Tammy for hands-on classes and special events.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events list */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          {loading && (
            <div className="text-center py-16 text-gray-400">Loading upcoming events…</div>
          )}

          {!loading && events.length === 0 && (
            <div className="text-center py-16">
              <CalendarIcon className="w-14 h-14 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold text-gray-600 mb-2">No upcoming events right now</h2>
              <p className="text-gray-500 mb-6">
                Check back soon — Tammy runs classes and events throughout the year.
              </p>
              <Link to="/contact" className="btn-primary">
                Get in touch to request a class
              </Link>
            </div>
          )}

          <div className="space-y-6">
            {events.map((evt, i) => (
              <motion.div
                key={evt.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className={`bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col md:flex-row ${
                  evt.is_featured ? 'ring-2 ring-bakery-rose' : ''
                }`}
              >
                {/* Image */}
                {evt.image_url ? (
                  <img
                    src={evt.image_url}
                    alt={evt.title}
                    className="w-full md:w-56 h-48 md:h-auto object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-full md:w-56 h-48 md:h-auto bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <span className="text-6xl">🎂</span>
                  </div>
                )}

                {/* Content */}
                <div className="p-6 flex flex-col justify-between flex-1">
                  <div>
                    {/* Category + featured badges */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {evt.category && (
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: evt.category.color + '22', color: evt.category.color }}
                        >
                          {evt.category.name}
                        </span>
                      )}
                      {evt.is_featured && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          ⭐ Featured
                        </span>
                      )}
                    </div>

                    <h2 className="text-xl font-bold text-gray-900 mb-3">{evt.title}</h2>

                    {/* Meta */}
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                      {evt.event_date && (
                        <span className="flex items-center gap-1.5">
                          <CalendarIcon className="w-4 h-4 text-bakery-rose" />
                          {formatDate(evt.event_date)}
                        </span>
                      )}
                      {evt.event_time && (
                        <span className="flex items-center gap-1.5">
                          <ClockIcon className="w-4 h-4 text-bakery-rose" />
                          {evt.event_time}
                        </span>
                      )}
                      {evt.location && (
                        <span className="flex items-center gap-1.5">
                          <MapPinIcon className="w-4 h-4 text-bakery-rose" />
                          {evt.location}
                        </span>
                      )}
                      {(evt.price_text || evt.price != null) && (
                        <span className="flex items-center gap-1.5 font-semibold text-gray-700">
                          {evt.price_text || `$${evt.price}`}
                        </span>
                      )}
                      {evt.spots_total != null && (
                        <span className="flex items-center gap-1.5">
                          <TicketIcon className="w-4 h-4 text-bakery-rose" />
                          {evt.spots_remaining != null
                            ? `${evt.spots_remaining} of ${evt.spots_total} spots left`
                            : `${evt.spots_total} spots`}
                        </span>
                      )}
                    </div>

                    {evt.description && (
                      <p className="text-gray-600 text-sm leading-relaxed">{evt.description}</p>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="mt-4 flex flex-wrap gap-3">
                    {evt.registration_url ? (
                      <a
                        href={evt.registration_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary text-sm py-2 px-5"
                      >
                        Register Now
                      </a>
                    ) : (
                      <Link to="/contact" className="btn-primary text-sm py-2 px-5">
                        Reserve My Spot
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
